import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  GraduationCap, BookOpen, TrendingUp, Users, DollarSign, 
  Briefcase, Target, Award, CheckCircle, Calendar, Clock,
  Lightbulb, PiggyBank, FileText, Megaphone, ShoppingBag,
  BarChart3, Handshake, Send, ArrowRight
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Course topics offered
const COURSE_TOPICS = [
  { 
    id: 'startup', 
    name: 'Starting a Business', 
    nameEs: 'Cómo Iniciar un Negocio',
    icon: Lightbulb, 
    description: 'Learn the fundamentals of launching your own business',
    color: 'bg-amber-100 text-amber-600'
  },
  { 
    id: 'finance', 
    name: 'Business Finance', 
    nameEs: 'Finanzas Empresariales',
    icon: PiggyBank, 
    description: 'Budgeting, bookkeeping, and financial management',
    color: 'bg-green-100 text-green-600'
  },
  { 
    id: 'marketing', 
    name: 'Marketing & Branding', 
    nameEs: 'Marketing y Marca',
    icon: Megaphone, 
    description: 'How to promote and grow your business',
    color: 'bg-purple/10 text-purple'
  },
  { 
    id: 'sales', 
    name: 'Sales Strategies', 
    nameEs: 'Estrategias de Ventas',
    icon: TrendingUp, 
    description: 'Techniques to increase your sales and revenue',
    color: 'bg-blue-100 text-blue-600'
  },
  { 
    id: 'legal', 
    name: 'Legal & Taxes', 
    nameEs: 'Legal e Impuestos',
    icon: FileText, 
    description: 'Business registration, licenses, and tax basics',
    color: 'bg-red-100 text-red-600'
  },
  { 
    id: 'ecommerce', 
    name: 'E-Commerce & Online Business', 
    nameEs: 'Comercio Electrónico',
    icon: ShoppingBag, 
    description: 'Selling products and services online',
    color: 'bg-cyan-100 text-cyan-600'
  },
  { 
    id: 'leadership', 
    name: 'Leadership & Management', 
    nameEs: 'Liderazgo y Gestión',
    icon: Users, 
    description: 'Building and leading a successful team',
    color: 'bg-indigo-100 text-indigo-600'
  },
  { 
    id: 'planning', 
    name: 'Business Planning', 
    nameEs: 'Planificación de Negocios',
    icon: Target, 
    description: 'Creating business plans and setting goals',
    color: 'bg-orange-100 text-orange-600'
  },
];

// Experience levels
const EXPERIENCE_LEVELS = [
  { value: 'beginner', label: 'Beginner - Just getting started', labelEs: 'Principiante - Recién comenzando' },
  { value: 'some', label: 'Some Experience - 1-2 years', labelEs: 'Algo de experiencia - 1-2 años' },
  { value: 'experienced', label: 'Experienced - 3+ years', labelEs: 'Experimentado - 3+ años' },
  { value: 'expert', label: 'Expert - Looking to expand', labelEs: 'Experto - Buscando expandir' },
];

export default function BusinessTeachingPage() {
  const { t, i18n } = useTranslation();
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [registrationType, setRegistrationType] = useState('student'); // 'student' or 'volunteer'
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    experience_level: '',
    current_business: '',
    goals: '',
    // Volunteer-specific fields
    teaching_experience: '',
    availability: '',
    why_teach: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const isSpanish = i18n.language === 'es';
  const isVolunteer = registrationType === 'volunteer';

  const toggleTopic = (topicId) => {
    setSelectedTopics(prev => {
      if (prev.includes(topicId)) {
        return prev.filter(id => id !== topicId);
      } else {
        return [...prev, topicId];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedTopics.length === 0) {
      toast.error(isSpanish ? 'Por favor seleccione al menos un tema' : 'Please select at least one topic');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/business-teaching`, {
        ...formData,
        topics: selectedTopics,
        registration_type: registrationType,
        language: i18n.language
      });
      
      setSubmitted(true);
      toast.success(isSpanish 
        ? (isVolunteer ? '¡Gracias por ofrecerte como voluntario!' : '¡Inscripción exitosa!') 
        : (isVolunteer ? 'Thank you for volunteering!' : 'Registration successful!')
      );
    } catch (error) {
      toast.error(isSpanish ? 'Error al enviar. Intente de nuevo.' : 'Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-cream pt-24 flex items-center justify-center" data-testid="business-teaching-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-lg mx-auto px-6"
        >
          <div className={`w-20 h-20 ${isVolunteer ? 'bg-burgundy' : 'bg-olive'} rounded-full flex items-center justify-center mx-auto mb-6`}>
            <CheckCircle size={40} className="text-white" />
          </div>
          <h1 className="font-serif text-3xl text-charcoal mb-4">
            {isVolunteer 
              ? (isSpanish ? '¡Gracias por Ofrecerte!' : 'Thank You for Volunteering!')
              : (isSpanish ? '¡Estás Inscrito!' : "You're Registered!")
            }
          </h1>
          <p className="text-charcoal/70 mb-6">
            {isVolunteer 
              ? (isSpanish 
                  ? 'Gracias por ofrecerte para enseñar en nuestro programa de negocios. Te contactaremos pronto para discutir los próximos pasos.'
                  : "Thank you for offering to teach in our business program. We'll contact you soon to discuss next steps and class scheduling.")
              : (isSpanish 
                  ? 'Gracias por inscribirte en nuestro programa de enseñanza de negocios. Te contactaremos pronto con más información sobre las próximas clases.'
                  : "Thank you for signing up for our business teaching program. We'll contact you soon with information about upcoming classes.")
            }
          </p>
          <div className="bg-gold/10 rounded-xl p-4 mb-8">
            <p className="text-sm text-charcoal/70">
              <strong>{isVolunteer 
                ? (isSpanish ? 'Temas que puedes enseñar:' : 'Topics you can teach:')
                : (isSpanish ? 'Temas seleccionados:' : 'Selected topics:')
              }</strong><br />
              {selectedTopics.map(id => {
                const topic = COURSE_TOPICS.find(t => t.id === id);
                return isSpanish ? topic?.nameEs : topic?.name;
              }).join(', ')}
            </p>
          </div>
          <Button
            onClick={() => {
              setSubmitted(false);
              setRegistrationType('student');
              setFormData({
                name: '',
                email: '',
                phone: '',
                experience_level: '',
                current_business: '',
                goals: '',
                teaching_experience: '',
                availability: '',
                why_teach: ''
              });
              setSelectedTopics([]);
            }}
            className="bg-burgundy hover:bg-purple text-white"
          >
            {isSpanish ? 'Registrar a Otra Persona' : 'Register Someone Else'}
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream pt-24" data-testid="business-teaching-page">
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 bg-gradient-to-br from-burgundy via-purple to-burgundy overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-40 h-40 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-60 h-60 bg-gold rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-6xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <GraduationCap size={20} className="text-gold" />
              <span className="text-white/90 text-sm font-medium">
                {isSpanish ? 'Programa de Educación Empresarial' : 'Business Education Program'}
              </span>
            </div>
            
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white mb-6">
              {isSpanish ? 'Aprende a Construir' : 'Learn to Build'}
              <span className="block text-gold">
                {isSpanish ? 'Tu Negocio' : 'Your Business'}
              </span>
            </h1>
            
            <p className="text-white/80 text-lg md:text-xl max-w-2xl mx-auto mb-8">
              {isSpanish 
                ? 'Clases gratuitas de negocios para emprendedores de nuestra comunidad. Aprende las habilidades que necesitas para tener éxito.'
                : 'Free business classes for entrepreneurs in our community. Learn the skills you need to succeed.'
              }
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Calendar size={16} className="text-gold" />
                <span className="text-white text-sm">{isSpanish ? 'Clases Semanales' : 'Weekly Classes'}</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <DollarSign size={16} className="text-gold" />
                <span className="text-white text-sm">{isSpanish ? '100% Gratis' : '100% Free'}</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Users size={16} className="text-gold" />
                <span className="text-white text-sm">{isSpanish ? 'Todos los Niveles' : 'All Levels'}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Learn With Us */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.2em] text-gold mb-4 block">
              {isSpanish ? 'POR QUÉ ELEGIRNOS' : 'WHY CHOOSE US'}
            </span>
            <h2 className="font-serif text-3xl md:text-4xl text-charcoal">
              {isSpanish ? 'Educación Empresarial Basada en la Fe' : 'Faith-Based Business Education'}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: BookOpen,
                title: isSpanish ? 'Currículo Práctico' : 'Practical Curriculum',
                description: isSpanish 
                  ? 'Aprende habilidades del mundo real que puedes aplicar inmediatamente a tu negocio.'
                  : 'Learn real-world skills you can apply immediately to your business.'
              },
              {
                icon: Handshake,
                title: isSpanish ? 'Mentoría Personalizada' : 'Personal Mentorship',
                description: isSpanish
                  ? 'Recibe orientación de empresarios experimentados en nuestra comunidad.'
                  : 'Receive guidance from experienced business owners in our community.'
              },
              {
                icon: Award,
                title: isSpanish ? 'Principios Bíblicos' : 'Biblical Principles',
                description: isSpanish
                  ? 'Construye tu negocio sobre fundamentos de integridad, servicio y excelencia.'
                  : 'Build your business on foundations of integrity, service, and excellence.'
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="text-center p-8"
              >
                <div className="w-16 h-16 bg-burgundy/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <item.icon size={32} className="text-burgundy" />
                </div>
                <h3 className="font-serif text-xl text-charcoal mb-3">{item.title}</h3>
                <p className="text-charcoal/70">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section className="py-16" id="signup">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-gold/10"
          >
            <div className="text-center mb-10">
              <div className="w-14 h-14 bg-gold/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <GraduationCap size={28} className="text-gold" />
              </div>
              <h2 className="font-serif text-2xl md:text-3xl text-charcoal mb-2">
                {isSpanish ? 'Inscríbete Ahora' : 'Sign Up Now'}
              </h2>
              <p className="text-charcoal/60">
                {isSpanish 
                  ? 'Completa el formulario para unirte a nuestras clases de negocios'
                  : 'Fill out the form to join our business classes'
                }
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Registration Type Selector */}
              <div className="mb-8">
                <Label className="text-charcoal font-medium mb-4 block text-lg">
                  {isSpanish ? '¿Cómo te gustaría participar?' : 'How would you like to participate?'}
                </Label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setRegistrationType('student')}
                    className={`p-6 rounded-xl border-2 transition-all duration-300 text-center ${
                      registrationType === 'student'
                        ? 'border-burgundy bg-burgundy/5 shadow-md'
                        : 'border-gold/20 bg-cream hover:border-gold/40'
                    }`}
                    data-testid="type-student"
                  >
                    <GraduationCap size={32} className={`mx-auto mb-3 ${registrationType === 'student' ? 'text-burgundy' : 'text-charcoal/50'}`} />
                    <h3 className="font-serif text-lg text-charcoal mb-1">
                      {isSpanish ? 'Quiero Aprender' : 'I Want to Learn'}
                    </h3>
                    <p className="text-sm text-charcoal/60">
                      {isSpanish ? 'Inscribirme como estudiante' : 'Sign up as a student'}
                    </p>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setRegistrationType('volunteer')}
                    className={`p-6 rounded-xl border-2 transition-all duration-300 text-center ${
                      registrationType === 'volunteer'
                        ? 'border-burgundy bg-burgundy/5 shadow-md'
                        : 'border-gold/20 bg-cream hover:border-gold/40'
                    }`}
                    data-testid="type-volunteer"
                  >
                    <Users size={32} className={`mx-auto mb-3 ${registrationType === 'volunteer' ? 'text-burgundy' : 'text-charcoal/50'}`} />
                    <h3 className="font-serif text-lg text-charcoal mb-1">
                      {isSpanish ? 'Quiero Enseñar' : 'I Want to Teach'}
                    </h3>
                    <p className="text-sm text-charcoal/60">
                      {isSpanish ? 'Voluntario para enseñar clases' : 'Volunteer to teach classes'}
                    </p>
                  </button>
                </div>
              </div>

              {/* Topic Selection */}
              <div>
                <Label className="text-charcoal font-medium mb-2 block text-lg">
                  {isVolunteer 
                    ? (isSpanish ? '¿Qué temas puedes enseñar?' : 'What topics can you teach?')
                    : (isSpanish ? '¿Qué temas te interesan?' : 'What topics interest you?')
                  }
                </Label>
                <p className="text-sm text-charcoal/60 mb-4">
                  {isSpanish ? 'Selecciona todos los que apliquen' : 'Select all that apply'}
                </p>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {COURSE_TOPICS.map((topic) => {
                    const Icon = topic.icon;
                    const isSelected = selectedTopics.includes(topic.id);
                    return (
                      <button
                        key={topic.id}
                        type="button"
                        onClick={() => toggleTopic(topic.id)}
                        className={`relative p-4 rounded-xl border-2 transition-all duration-300 text-left ${
                          isSelected 
                            ? 'border-burgundy bg-burgundy/5 shadow-md' 
                            : 'border-gold/20 bg-cream hover:border-gold/40'
                        }`}
                        data-testid={`topic-${topic.id}`}
                      >
                        <div className={`absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center ${
                          isSelected ? 'bg-burgundy' : 'bg-gray-200'
                        }`}>
                          {isSelected && <CheckCircle size={14} className="text-white" />}
                        </div>
                        
                        <div className={`w-10 h-10 rounded-lg ${topic.color} flex items-center justify-center mb-2`}>
                          <Icon size={20} />
                        </div>
                        <h3 className="font-medium text-charcoal text-sm leading-tight">
                          {isSpanish ? topic.nameEs : topic.name}
                        </h3>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Personal Information */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>{isSpanish ? 'Nombre Completo' : 'Full Name'} *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    className="bg-cream border-gold/20 focus:border-gold"
                    placeholder={isSpanish ? 'Tu nombre' : 'Your name'}
                  />
                </div>
                <div>
                  <Label>{isSpanish ? 'Correo Electrónico' : 'Email'} *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    className="bg-cream border-gold/20 focus:border-gold"
                    placeholder="email@example.com"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>{isSpanish ? 'Teléfono' : 'Phone'}</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="bg-cream border-gold/20 focus:border-gold"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <Label>{isSpanish ? 'Nivel de Experiencia' : 'Experience Level'} *</Label>
                  <Select 
                    value={formData.experience_level} 
                    onValueChange={(v) => setFormData({...formData, experience_level: v})}
                  >
                    <SelectTrigger className="bg-cream border-gold/20">
                      <SelectValue placeholder={isSpanish ? 'Seleccionar nivel' : 'Select level'} />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPERIENCE_LEVELS.map(level => (
                        <SelectItem key={level.value} value={level.value}>
                          {isSpanish ? level.labelEs : level.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Student-specific fields */}
              {!isVolunteer && (
                <>
                  <div>
                    <Label>{isSpanish ? '¿Tienes un negocio actualmente?' : 'Do you currently have a business?'}</Label>
                    <Input
                      value={formData.current_business}
                      onChange={(e) => setFormData({...formData, current_business: e.target.value})}
                      className="bg-cream border-gold/20 focus:border-gold"
                      placeholder={isSpanish ? 'Describe tu negocio o idea de negocio' : 'Describe your business or business idea'}
                    />
                  </div>

                  <div>
                    <Label>{isSpanish ? '¿Cuáles son tus metas?' : 'What are your goals?'}</Label>
                    <Textarea
                      value={formData.goals}
                      onChange={(e) => setFormData({...formData, goals: e.target.value})}
                      className="bg-cream border-gold/20 focus:border-gold"
                      placeholder={isSpanish 
                        ? '¿Qué esperas lograr con estas clases?' 
                        : 'What do you hope to achieve from these classes?'
                      }
                      rows={3}
                    />
                  </div>
                </>
              )}

              {/* Volunteer-specific fields */}
              {isVolunteer && (
                <>
                  <div>
                    <Label>{isSpanish ? 'Tu Negocio o Experiencia Profesional' : 'Your Business or Professional Experience'} *</Label>
                    <Input
                      value={formData.current_business}
                      onChange={(e) => setFormData({...formData, current_business: e.target.value})}
                      required
                      className="bg-cream border-gold/20 focus:border-gold"
                      placeholder={isSpanish ? 'Ej: Dueño de restaurante por 10 años' : 'E.g., Restaurant owner for 10 years'}
                    />
                  </div>

                  <div>
                    <Label>{isSpanish ? 'Experiencia Enseñando o Mentoreando' : 'Teaching or Mentoring Experience'}</Label>
                    <Textarea
                      value={formData.teaching_experience}
                      onChange={(e) => setFormData({...formData, teaching_experience: e.target.value})}
                      className="bg-cream border-gold/20 focus:border-gold"
                      placeholder={isSpanish 
                        ? 'Describe cualquier experiencia previa enseñando, capacitando o mentoreando a otros...'
                        : 'Describe any previous experience teaching, training, or mentoring others...'
                      }
                      rows={3}
                    />
                  </div>

                  <div>
                    <Label>{isSpanish ? 'Disponibilidad' : 'Availability'} *</Label>
                    <Input
                      value={formData.availability}
                      onChange={(e) => setFormData({...formData, availability: e.target.value})}
                      required
                      className="bg-cream border-gold/20 focus:border-gold"
                      placeholder={isSpanish ? 'Ej: Sábados por la mañana, Noches entre semana' : 'E.g., Saturday mornings, Weekday evenings'}
                    />
                  </div>

                  <div>
                    <Label>{isSpanish ? '¿Por qué quieres enseñar?' : 'Why do you want to teach?'}</Label>
                    <Textarea
                      value={formData.why_teach}
                      onChange={(e) => setFormData({...formData, why_teach: e.target.value})}
                      className="bg-cream border-gold/20 focus:border-gold"
                      placeholder={isSpanish 
                        ? '¿Qué te motiva a compartir tu conocimiento con la comunidad?'
                        : 'What motivates you to share your knowledge with the community?'
                      }
                      rows={3}
                    />
                  </div>
                </>
              )}

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-burgundy hover:bg-purple text-white py-6 text-lg font-medium rounded-xl"
                data-testid="submit-button"
              >
                {isSubmitting ? (
                  <span>{isSpanish ? 'Enviando...' : 'Submitting...'}</span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    {isVolunteer 
                      ? (isSpanish ? 'Ofrecerme como Voluntario' : 'Volunteer to Teach')
                      : (isSpanish ? 'Inscribirme' : 'Sign Me Up')
                    }
                    <ArrowRight size={20} />
                  </span>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </section>

      {/* What You'll Learn */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.2em] text-gold mb-4 block">
              {isSpanish ? 'PLAN DE ESTUDIOS' : 'CURRICULUM'}
            </span>
            <h2 className="font-serif text-3xl md:text-4xl text-charcoal">
              {isSpanish ? 'Lo Que Aprenderás' : "What You'll Learn"}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {COURSE_TOPICS.map((topic, i) => {
              const Icon = topic.icon;
              return (
                <motion.div
                  key={topic.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  viewport={{ once: true }}
                  className="flex items-start gap-4 p-6 bg-cream rounded-2xl"
                >
                  <div className={`w-12 h-12 rounded-xl ${topic.color} flex items-center justify-center flex-shrink-0`}>
                    <Icon size={24} />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg text-charcoal mb-1">
                      {isSpanish ? topic.nameEs : topic.name}
                    </h3>
                    <p className="text-charcoal/70 text-sm">{topic.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials / Success Stories */}
      <section className="py-16 bg-burgundy">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.2em] text-gold mb-4 block">
              {isSpanish ? 'HISTORIAS DE ÉXITO' : 'SUCCESS STORIES'}
            </span>
            <h2 className="font-serif text-3xl md:text-4xl text-white">
              {isSpanish ? 'Nuestros Graduados' : 'Our Graduates'}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                quote: isSpanish 
                  ? 'Gracias a este programa, pude abrir mi propio salón de belleza. ¡Las clases de finanzas fueron invaluables!'
                  : "Thanks to this program, I was able to open my own beauty salon. The finance classes were invaluable!",
                name: 'Maria G.',
                business: isSpanish ? 'Dueña de Salón de Belleza' : 'Beauty Salon Owner'
              },
              {
                quote: isSpanish
                  ? 'Aprendí cómo registrar mi negocio y manejar los impuestos. Ahora tengo confianza para crecer.'
                  : "I learned how to register my business and handle taxes. Now I have confidence to grow.",
                name: 'Carlos R.',
                business: isSpanish ? 'Contratista de Paisajismo' : 'Landscaping Contractor'
              },
              {
                quote: isSpanish
                  ? 'Las clases de marketing me ayudaron a conseguir mis primeros 50 clientes en solo 3 meses.'
                  : "The marketing classes helped me get my first 50 clients in just 3 months.",
                name: 'Ana L.',
                business: isSpanish ? 'Servicios de Limpieza' : 'Cleaning Services'
              },
            ].map((testimonial, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6"
              >
                <p className="text-white/90 mb-4 italic">"{testimonial.quote}"</p>
                <div>
                  <p className="text-gold font-medium">{testimonial.name}</p>
                  <p className="text-white/60 text-sm">{testimonial.business}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Class Schedule Info */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg border border-gold/10">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <span className="text-xs uppercase tracking-[0.2em] text-gold mb-4 block">
                  {isSpanish ? 'INFORMACIÓN DE CLASES' : 'CLASS INFO'}
                </span>
                <h2 className="font-serif text-2xl md:text-3xl text-charcoal mb-4">
                  {isSpanish ? 'Horario de Clases' : 'Class Schedule'}
                </h2>
                <p className="text-charcoal/70 mb-6">
                  {isSpanish
                    ? 'Nuestras clases se ofrecen en horarios flexibles para acomodar tu agenda.'
                    : 'Our classes are offered at flexible times to accommodate your schedule.'
                  }
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Calendar className="text-burgundy" size={20} />
                    <span className="text-charcoal">
                      {isSpanish ? 'Sábados 9:00 AM - 12:00 PM' : 'Saturdays 9:00 AM - 12:00 PM'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="text-burgundy" size={20} />
                    <span className="text-charcoal">
                      {isSpanish ? 'Programa de 8 semanas' : '8-week program'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Users className="text-burgundy" size={20} />
                    <span className="text-charcoal">
                      {isSpanish ? 'Grupos pequeños (máx. 15)' : 'Small groups (max 15)'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-gold/20 to-burgundy/10 rounded-2xl p-6 text-center">
                <BarChart3 size={48} className="text-burgundy mx-auto mb-4" />
                <h3 className="font-serif text-xl text-charcoal mb-2">
                  {isSpanish ? 'Próxima Clase' : 'Next Class'}
                </h3>
                <p className="text-charcoal/70 mb-4">
                  {isSpanish ? 'Las inscripciones están abiertas' : 'Registration is now open'}
                </p>
                <a 
                  href="#signup"
                  className="inline-flex items-center gap-2 bg-burgundy text-white px-6 py-3 rounded-xl hover:bg-purple transition-colors"
                >
                  {isSpanish ? 'Inscribirme Ahora' : 'Sign Up Now'}
                  <ArrowRight size={18} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
